import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from "react";
import { Step, Steps, Wizard } from "react-albus";
import { Button, Col, Form, Modal, ProgressBar, Row, Tab, Alert, Tabs, Dropdown, DropdownButton } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import * as Common from "../../helpers/data/CommonImport";
import { getSuppliers } from "../../redux/slices/Suppliers";
import useToggle from "../../hooks/useToggle";
import { Spinner, FileUploader, ProgressStepBar } from "../../components";
import * as importWizardTools from "../../helpers/data/importWizardTools";
import { AgGrid, AgGridRef, AgGridRowHeight, ZColDef } from "../../components/AgGrid/AgGrid";
import { ColGroupDef, IHeaderParams } from "ag-grid-enterprise";
import { Search } from "../../components/AgGrid/FilterComponents/Search";
import { ProcessWorkerRequestData, ProcessWorkerResponseData } from "./processWorker";
import axios from "axios";
import { getToken, logout } from "../../helpers/api/authService";
import { WorkerResponse, runBatchWorkers } from "../../components/WebWorkers/ZWebWorker";
import { ProcessingState, ValidationState } from "./ImportWizardTypes";
import { ZCheckboxButton } from "components/ZCheckboxButton";
import ReactToPrint from "react-to-print";
import { FormatDateToDateTime, FormatDateToFileName } from "helpers/data/dataHandling";
import { Z } from "models/common/components";
import { hasDuplicates } from "helpers/Functions";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { Controller, useForm } from "react-hook-form";
import { ZIconType } from "helpers/utility/iconMap";
import { ZIcon } from "components/ZIcon";
import { ZTooltip } from "components/ZTooltip";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { SupplierNameMapping } from "models/dbModels/supplierNameMappings/SupplierNameMapping";
import * as XLSX from "xlsx";

export type MatchAgainstOptions = "Products" | "Stocks" | "None" | "Pick" | "Both";

export type ProcessingResult = "No Match" | "Success" | "Duplicate" | "Multiple Matches" | "Error";

type PostImportColumnDefs = {
	field: { key: string; header: string }; // For example if we want post import screen to show department name instead of stock name
	errorKey: string;
};

export enum ImportBy {
	APN = "APN",
	PDE = "PDE",
	ANP = "ANP",
}

export interface ImportWizardProps {
	title?: string;
	//Display the moodal
	display: boolean;
	//What happens when you close?
	handleClose: () => void;
	//Event when you import the rows
	import?: (rows: any[]) => any[];
	importAsync?: (rows: any[]) => Promise<any[]>;
	//Validating the whole table at once, make sure you set has error and message if required
	validateTable?: (rows: CommonColumnProperties[]) => CommonColumnProperties[];

	//for processing the rows
	postProcessStep?: (rows: CommonColumnProperties[]) => CommonColumnProperties[];

	finish: () => void;
	//For controlling the PDE, APN and Suppliercode
	matchAgainst: MatchAgainstOptions;
	pdeColumn?: IdentifierColumns;
	apnColumn?: IdentifierColumns;
	supplierColumn?: IdentifierColumns;
	//Custom columns
	customColumns?: CustomColumns[];
	// Displays the percentage of the import
	importPercentage?: number;
	importLabel?: string;
	customPostImportColumnDef?: PostImportColumnDefs;
	// Custom options on the matching page
	additionalSettings?: ImportWizardSetting[];
	onSettingsSubmit?: () => void;
	onSettingsReset?: () => void;
	stepChange?: (step: number) => void;
	matchedColumnOverride?: ZColDef;

	showAllImportByColumns?: boolean; // Show PDE, APN, Supplier columns regardless of importBy setting, this should only be needed by managed stocks

	overrideImportBy?: ImportBy; // Override the importBy setting, this should only be needed by managed stocks
}

export interface IdentifierColumns {
	display?: boolean;
	defaultColumn?: number;
	validation?: (value: any) => { valid: boolean; error: string; newValue?: any };
}

export interface CustomColumns {
	title: string;
	key: string;
	required?: boolean;
	display?: boolean;
	defaultColumn?: number;
	validation?: (value: any, fileFormat: FormatType) => { valid: boolean; error: string; newValue?: any; displayValue?: any };
	autoMatch?: string[];
}

export type MatchedAgainst = "System" | "Managed" | "N/A" | "None";

export interface CommonColumnProperties extends Record<string, any> {
	processed?: ProcessingState;
	message?: string;
	hasError?: boolean;
	id?: string;
	stockId?: string;
	name?: string;
	matchedAgainst?: MatchedAgainst;
	result?: ProcessingResult;
}

/**
 * Defines an additional option to present on the settings page of the import wizard
 */
export type ImportWizardSetting = {
	/**
	 * Must be unique across additional settings
	 */
	key: string;
	/**
	 * The element to render for the setting
	 */
	element: JSX.Element;
};

// option must be some renderable JSX.Element / React Node
// Option must have some sort of callback that fires when the options are submitted to fetch the value to pass along
// Other than that, there's no reason to restrict the option.

export type FormatType = "csv" | "xls";

export interface ImportWizardRef {
	Reset: () => void;
	UpdateColumns: (columns: CustomColumns[]) => void;
	getSetting: (key: "ImportBy" | "CurrentSheet") => any;
}

export const ImportWizard = forwardRef<ImportWizardRef, ImportWizardProps>((props, ref) => {
	const dispatch = useAppDispatch();
	const suppliers = useAppSelector((state: RootState) => state.suppliers);
	const supplierNameMappings = useAppSelector((state: RootState) => state.supplierNameMappings);
	const previewGridRef = useRef<AgGridRef>(null);
	const validLinesRef = useRef<AgGridRef>(null);
	const invalidLinesRef = useRef<AgGridRef>(null);
	//Column selections
	const [columnValues, setColumnValues] = useState<(number | undefined)[]>([]);
	const [columnOptions, setColumnOptions] = useState<CustomColumns[]>([]);
	const columnValuesRef = useRef<(number | undefined)[]>([]);
	const [formatType, setFormatType] = useState<FormatType | undefined>();
	const [fileSheets, setFileSheets] = useState<string[]>([]);
	const [currentSheet, setCurrentSheet] = useState<string | undefined>(undefined);
	const [fileWorkBook, setFileWorkbook] = useState<Common.DataSet | undefined>();
	const [fileWorkBookRange, setFileWorkbookRange] = useState<{ s: { c: number; r: number }; e: { c: number; r: number } } | undefined>(undefined);
	const [fileLoaded, setFileLoaded] = useState<boolean>(false);
	const [stepNumber, setStepNumber] = useState<number>(1);
	const [fileDetails, setFileDetails] = useState<{ name: string; size: string }>({ name: "", size: "" });
	const [loading, setLoading] = useState<boolean>(false);
	const [validating, setValidating] = useState<boolean>(false);
	const [isProcessing, setIsProcessing] = useState<boolean>(false);
	const [importWizardTable, setImportWizardTable] = useState<any[]>([]);
	const [importWizardTableColumns, setImportWizardTableColumns] = useState<any[]>([]);
	const [headerRow, setHeaderRow] = useState<number>(1);
	const [validatedWizardTable, setValidatedWizardTable] = useState<any[]>([]);
	const [finalTable, setFinalTable] = useState<any[]>([]);
	const [postImportTable, setPostImportTable] = useState<any[]>([]);
	const [importBy, setImportBy] = useState<ImportBy | undefined>(props.overrideImportBy);
	const [totalStocksToImport, setTotalStocksToImport] = useState<number>(0);
	const [processedStocks, setProcessedStocks] = useState<number>(0);
	const [readyToValidate, setReadyToValidate] = useState<boolean>(false);
	const [readyToProcess, setReadyToProcess] = useState<boolean>(false);
	const [progressPercentage, setProgressPercentage] = useState<number>(0);
	const [readyToImport, setReadyToImport] = useState<boolean>(false);
	const [showImportButton, setShowImportButton] = useState<boolean>(true);
	const [validationColumns, setValidationColumns] = useState<ZColDef[]>([]);
	const [reimportableRows, setReimportableRows] = useState<any[]>([]);
	// const [previewColumns, setPreviewColumns] = useState<any[]>([]);
	const [reviewColumns, setReviewColumns] = useState<ZColDef[]>([]);
	const [postImportColumns, setPostImportColumns] = useState<ZColDef[]>([]);
	const [matchAgainst, setMatchAgainst] = useState<MatchAgainstOptions>("None");
	const [closeButton, setCloseButton] = useState<boolean>(true);
	const [keyword, setKeyword] = useState<string>("");
	const closing = useRef<boolean>(false);

	const importByMap: Map<ImportBy, string> = new Map<ImportBy, string>([
		[ImportBy.APN, "Match by APN"],
		[ImportBy.PDE, "Match by PDE"],
		[ImportBy.ANP, "Match by APN or PDE"],
	]);

	const apnColumn = "__apn";
	const pdeColumn = "__pde";
	const supplierColumn = "__supplier";
	const defaultSettings = {
		fileSheets: [],
		currentSheet: undefined,
		fileWorkBook: undefined,
		fileLoaded: false,
		isProcessing: false,
		loading: false,
		fileDetails: { name: "", size: "" },
		importWizardTable: [],
		importWizardTableColumns: [],
		columnsList: [],
		importBy: props.overrideImportBy,
		step: 1,
		totalStocksToImport: 0,
		apnColumn: "",
		pdeColumn: "",
		supplierColumn: "",
		processedStocks: 0,
		readyToProcess: false,
		readyToImport: false,
		readyToValidate: false,
		stepNumber: 1,
		closeButton: true,
		progressPercentage: 0,
		finalTable: [],
		closing: false,
		matchingOptions: [],
	};

	const {
		handleSubmit,
		control,
		reset,
		getValues,
		setValue,
		setError,
		trigger,
		formState: { errors },
	} = useForm<{ importBy: string }>({
		defaultValues: defaultSettings,
		mode: "onChange",
		reValidateMode: "onChange",
	});

	function Reset() {
		reset(defaultSettings);
		setProgressPercentage(0);
		setColumnValues([]);
		setColumnOptions([]);
		setShowImportButton(true);
		columnValuesRef.current = [];
		setFileSheets(defaultSettings.fileSheets);
		setFormatType(undefined);
		setCurrentSheet(defaultSettings.currentSheet);
		setFileWorkbook(defaultSettings.fileWorkBook);
		setFileWorkbookRange(undefined);
		setFileLoaded(defaultSettings.fileLoaded);
		setFileDetails(defaultSettings.fileDetails);
		setLoading(defaultSettings.loading);
		setIsProcessing(defaultSettings.isProcessing);
		setImportWizardTable(defaultSettings.importWizardTable);
		setImportWizardTableColumns(defaultSettings.importWizardTableColumns);
		setHeaderRow(1);
		setValidatedWizardTable(defaultSettings.importWizardTable);
		setImportBy(props.overrideImportBy);
		setTotalStocksToImport(defaultSettings.totalStocksToImport);
		setProcessedStocks(defaultSettings.processedStocks);
		setReadyToValidate(defaultSettings.readyToValidate);
		setReadyToProcess(defaultSettings.readyToProcess);
		setReadyToImport(defaultSettings.readyToImport);
		setValidationColumns([]);
		setReviewColumns([]);
		setPostImportTable([]);
		setPostImportColumns([]);
		ResetColumns();
		setKeyword("");
		setMatchAgainst(props.matchAgainst);
		setStepNumber(defaultSettings.stepNumber);
		props.stepChange && props.stepChange(defaultSettings.stepNumber);
		setCloseButton(defaultSettings.closeButton);
		setProgressPercentage(defaultSettings.progressPercentage);
		setFinalTable(defaultSettings.finalTable);

		props.onSettingsReset && props.onSettingsReset();

		if (props.display) closing.current = defaultSettings.closing;
	}

	function testAdditionalOptionKeyUniqueness() {
		if (props.additionalSettings) {
			if (hasDuplicates(props.additionalSettings.map((s) => s.key))) {
				throw `Additional options supplied to import wizard ${props.title} contain non-unique keys!\n\nIt is very important that the keys for these options are unique.`;
			}
		}
	}

	interface IMappingColumnHeaderParams extends IHeaderParams {
		index: number;
		column: any;
		originalName: string;
	}

	const MappingColumnHeader = (props: IMappingColumnHeaderParams) => {
		const value = importWizardTools.columnsOptionsValue(props.index, columnValuesRef.current);
		//But why we using Ref you says! Cause React won"t render the new values!!!! So we"ll use ref and fake a refresh with the other state

		return (
			<div>
				{columnOptions.filter((x) => x.display === undefined || x.display === true).length > 0 && (
					<Form.Select
						style={{ minHeight: "30px" }}
						className="custom-select mb-1"
						value={value}
						onChange={(e) => {
							let temp = [...columnValuesRef.current];
							let index = parseInt(e.target.value);
							columnValuesRef.current.forEach((v, i) => {
								if (v == props.index) {
									temp[i] = undefined;
								}
							});
							temp[index] = props.index;
							setColumnValues(temp);
							columnValuesRef.current = temp;
						}}
					>
						<option value=""> </option>
						{columnOptions
							.filter((x) => x.display === undefined || x.display === true)
							.map((columnOption: CustomColumns) => {
								return (
									<option key={columnOption.key} value={columnOptions.indexOf(columnOption)}>
										{columnOption.title}
									</option>
								);
							})}
					</Form.Select>
				)}
			</div>
		);
	};

	useImperativeHandle(ref, () => ({
		Reset: () => Reset(),
		UpdateColumns: (columns: CustomColumns[]) => {
			setColumnOptions(columns);
			ResetColumns();
		},
		getSetting: (key: "ImportBy" | "CurrentSheet") => {
			switch (key) {
				case "ImportBy":
					return importBy;
				case "CurrentSheet":
					return currentSheet;
				default:
					return undefined;
			}
		},
	}));

	useEffect(() => {
		if (!closeButton) {
			const handleBeforeUnload = (event: any) => {
				const message = "Are you sure you want to leave before the import is complete?";
				event.returnValue = message;
				event.preventDefault();
				return message;
			};
			window.addEventListener("beforeunload", handleBeforeUnload);
			return () => {
				window.removeEventListener("beforeunload", handleBeforeUnload);
			};
		}
	}, [closeButton]);

	useEffect(() => {
		let defaultColumns: (number | undefined)[] = [];
		columnOptions.forEach((v, i) => {
			if (v.defaultColumn !== undefined) {
				defaultColumns.push(v.defaultColumn);
			} else if (v.autoMatch !== undefined) {
				let options = v.autoMatch.map((i) => i.toLocaleLowerCase());
				let defValue: number | undefined = undefined; //Default
				importWizardTableColumns.forEach((x, i) => {
					if (options.includes(x.columnName?.toLocaleLowerCase())) {
						defValue = i;
					}
				});
				defaultColumns.push(defValue);
			} else {
				defaultColumns.push(undefined);
			}
		});
		columnValuesRef.current = defaultColumns;
		setColumnValues(columnValuesRef.current);

		setValidationColumns([
			{
				headerName: "Valid",
				field: "validationState",
				sortable: true,
				pinned: true,
				cellRendererSelector: (params) => {
					if (params.value === ValidationState.Validating) {
						return { component: "validCellRenderer", params: { loading: true } };
					} else {
						return {
							component: "validCellRenderer",
							params: { value: params.value === undefined ? params.value : params.value === ValidationState.Valid, validationError: params.data.message },
						};
					}
				},
				// Cell: (row: any) => <>
				//     {row.value === true && <i className="uil-times-circle"></i>}
				//     {row.value === false && <i className="uil-check"></i>}
				//     {row.value === null && <i className="uil-spinner"></i>}
				//     &nbsp;
				//     {row.row.original.message}
				// </>,
			},
			...importWizardTableColumns.map<ColGroupDef>((column: any, index: number) => {
				const newHeaderName = (fileWorkBook && currentSheet && importWizardTools.getFileWorkBookHerader(index, headerRow, fileWorkBook[currentSheet], fileWorkBookRange?.s?.c)) || "-";
				return {
					children: [
						{
							headerName: newHeaderName,
							sortable: true,
							field: column.accessor,
						},
					],
					field: column.accessor,
					headerName: column.columnName,
					headerGroupComponent: MappingColumnHeader,
					headerGroupComponentParams: {
						column: column,
						originalName: column.columnName,
						index: index,
					},
					minWidth: 100,
				};
			}),
		]);
		// setValidationColumns([
		//     {
		//         Header: "Valid",
		//         accessor: "hasError",
		//         fixed: "left",
		//         Cell: (row: any) => <>
		//             {row.value === true && <i className="uil-times-circle"></i>}
		//             {row.value === false && <i className="uil-check"></i>}
		//             {row.value === null && <i className="uil-spinner"></i>}
		//             &nbsp;
		//             {row.row.original.message}
		//             {row.value}
		//         </>,
		//     },
		//     ...importWizardTableColumns.map((column: any, index: number) => {
		//         return {
		//             Header: <><ColumnHeader column={column} originalName={column.columnName} index={index} /></>,
		//             minWidth: 250,
		//             accessor: column.accessor,
		//             Cell: (row: any) => <>{row.value}</>,
		//         }
		//     }),
		// ])

		// setPreviewColumns([
		//     {
		//         Header: "Matched",
		//         accessor: "processed",
		//         Cell: (row: any) => <>
		//             {row.value === true && <i className="uil-check"></i>}
		//             {row.value === false && <i className="uil-times-circle"></i>}
		//             &nbsp;
		//             {row.row.original.message}
		//         </>,
		//     },
		//     ...importWizardTableColumns.map((column: any, index: number) => {
		//         return {
		//             Header: <><ColumnHeader column={column} originalName={column.columnName} index={index} /></>,
		//             minWidth: 250,
		//             accessor: column.accessor,
		//             Cell: (row: any) => <>{row.value}</>,
		//         }
		//     }),
		// ])

		// setReviewColumns([
		//     {
		//         headerName: "Matched",
		//         field: "processed",
		//         pinned: true,
		//         cellRendererSelector: (params) => {
		//             if (params.value === ProcessingState.Processing) {
		//                 return { component: 'validCellRenderer', params: { loading: true } };
		//             } else {
		//                 return { component: 'validCellRenderer', params: { value: params.value === undefined ? params.value : params.value === ProcessingState.Done,  validationError: params.data.message } };
		//             }
		//         },
		//         // Cell: (row: any) => <>
		//         //     {row.value === true && <i className="uil-times-circle"></i>}
		//         //     {row.value === false && <i className="uil-check"></i>}
		//         //     {row.value === null && <i className="uil-spinner"></i>}
		//         //     &nbsp;
		//         //     {row.row.original.message}
		//         // </>,
		//     },
		//     ...importWizardTableColumns.map<ZColDef>((column: any, index: number) => {
		//         const newHeaderName = fileWorkBook && currentSheet && importWizardTools.getFileWorkBookHerader(index, headerRow, fileWorkBook[currentSheet]) || "-";
		//         return {
		//             headerName: newHeaderName,
		//             sortable: true,
		//             field: column.accessor,
		//             minWidth: 100,
		//         }
		//     })
		// ])

		setPostImportColumns([
			{
				headerName: props.customPostImportColumnDef ? props.customPostImportColumnDef.field.header : "Stock Name",
				field: props.customPostImportColumnDef ? props.customPostImportColumnDef.field.key : "stockName",
				pinned: true,
				sortable: true,
				flex: 1,
				// cellRendererSelector: (params) => {
				//     if (params.value === ProcessingState.Processing) {
				//         return { component: 'validCellRenderer', params: { loading: true } };
				//     } else {
				//         return { component: 'validCellRenderer', params: { value: params.value === undefined ? params.value : params.value === ProcessingState.Done,  validationError: params.data.message } };
				//     }
				// },
				// Cell: (row: any) => <>
				//     {row.value === true && <i className="uil-times-circle"></i>}
				//     {row.value === false && <i className="uil-check"></i>}
				//     {row.value === null && <i className="uil-spinner"></i>}
				//     &nbsp;
				//     {row.row.original.message}
				// </>,
			},
			{
				headerName: "Errors",
				field: props.customPostImportColumnDef ? props.customPostImportColumnDef.errorKey : "error",
				flex: 1,
				cellRendererSelector: (params) => {
					return { component: "validCellRenderer", params: { validationError: params.data.error } };
				},
			},
		]);
	}, [importWizardTableColumns, columnOptions, headerRow, fileWorkBook, currentSheet]);

	useEffect(() => {
		if (currentSheet) {
			selectSheet(currentSheet);

			if (fileWorkBook) setFileWorkbookRange(XLSX.utils.decode_range(fileWorkBook[currentSheet]["!ref"]!));

			dispatch(getSuppliers());
		}
	}, [fileLoaded, currentSheet, headerRow]);

	useEffect(() => {
		if (props.display) {
			Reset();
		}
	}, [props.display]);

	function validateColumns() {
		let readyToValidate = true;
		if (matchAgainst !== "None" && importBy === undefined) readyToValidate = false;
		columnOptions.forEach((v, i) => {
			let selected = columnValues[i];
			const columnRequired = isColumnRequired(v);
			if (columnRequired) {
				readyToValidate = readyToValidate && selected !== undefined;
			}
		});
		setValidatedWizardTable(DeepCopy(importWizardTable));
		setReadyToValidate(readyToValidate);
		setReadyToProcess(false);
	}

	/**
	 * We do have a required field on the column but with the structure of the import wizard it's quite limited and cannot reference other columns, so we perform some additional checks here
	 * @param column The column to check if it's required
	 * @returns Whether the column is required or not
	 */
	function isColumnRequired(column: CustomColumns): boolean {
		if (importBy === ImportBy.ANP) {
			// If we are looking at the APN column but PDE and Supplier are set, we don't require the APN column
			if (column.key === apnColumn) {
				// Check column values to see if PDE or supplier are set
				const pdeColumnIndex = columnOptions.findIndex((v) => v.key === pdeColumn);
				const supplierColumnIndex = columnOptions.findIndex((v) => v.key === supplierColumn);
				// If they aren't, we require the APN column
				return columnValues[pdeColumnIndex] === undefined && columnValues[supplierColumnIndex] === undefined;
			} else if (column.key === pdeColumn || column.key === supplierColumn) {
				// Conversely, if we are looking at the PDE or supplier columns but APN is set, we don't require the PDE/supplier columns
				const apnColumnIndex = columnOptions.findIndex((v) => v.key === apnColumn);
				// Check column values to see if APN is set
				// If it isn't, we require the PDE/supplier columns
				return columnValues[apnColumnIndex] === undefined;
			}
		}

		return column.required === undefined || column.required === true;
	}

	useEffect(() => {
		validateColumns();
	}, [importBy, columnValues]);

	function ResetColumns() {
		let columnOptions: CustomColumns[] = [];
		if (matchAgainst !== "None") {
			let apnValidator = (cellValue: any) => {
				let cleanCellValue = Common.CleanValue(cellValue);
				const isValid = importBy !== "APN" || cleanCellValue;

				return isValid ? { valid: true, error: "", newValue: cleanCellValue } : { valid: false, error: "Apn is invalid" };
			};
			let pdeValidator = (cellValue: any) => {
				let cleanCellValue = Common.CleanValue(cellValue);
				const isValid = importBy !== "PDE" || cleanCellValue;

				return isValid ? { valid: true, error: "", newValue: cleanCellValue } : { valid: false, error: "Pde is invalid" };
			};
			let supplierValidator = (cellValue: any) => {
				let supplierId = findSupplierId(cellValue);

				// Supplier cell is valid if:
				// If its got a value, we must validate (since update will trigger this)
				// - We didn't supply a cell value (empty supplier)
				// - The supplier code that was passed in was found
				const isValid = !cellValue || supplierId;

				return isValid ? { valid: true, error: "", newValue: supplierId, displayValue: cellValue } : { valid: false, error: "Supplier is invalid" };
			};
			if (importBy === ImportBy.APN || importBy === ImportBy.ANP || props.showAllImportByColumns) {
				columnOptions.push({
					title: "APN",
					key: apnColumn,
					defaultColumn: props.apnColumn?.defaultColumn,
					display: props.apnColumn?.display,
					required: importBy === "APN" || importBy === "ANP",
					validation: props.apnColumn?.validation ?? apnValidator,
					autoMatch: ["apn", "barcode"],
				});
			}
			if (importBy === ImportBy.PDE || importBy === ImportBy.ANP || props.showAllImportByColumns) {
				columnOptions.push({
					title: "PDE",
					key: pdeColumn,
					defaultColumn: props.pdeColumn?.defaultColumn,
					display: props.pdeColumn?.display,
					required: importBy === "PDE" || importBy === "ANP",
					validation: props.pdeColumn?.validation ?? pdeValidator,
					autoMatch: ["pde", "supplier code"],
				});
				columnOptions.push({
					title: "Supplier",
					key: supplierColumn,
					defaultColumn: props.supplierColumn?.defaultColumn,
					display: props.supplierColumn?.display,
					required: importBy === "PDE" || importBy === "ANP",
					validation: props.supplierColumn?.validation ?? supplierValidator,
					autoMatch: ["supplier"],
				});
			}
		}
		if (props.customColumns) columnOptions.push(...props.customColumns);
		setColumnOptions(columnOptions);
	}

	useEffect(() => {
		ResetColumns();
	}, [importBy, props.apnColumn, props.pdeColumn, props.supplierColumn, props.customColumns, matchAgainst]);

	useEffect(() => {
		setMatchAgainst(props.matchAgainst);
	}, [props.matchAgainst]);

	useEffect(() => {
		testAdditionalOptionKeyUniqueness();
	}, [props.additionalSettings]);

	function DeepCopy(value: any): any {
		return JSON.parse(JSON.stringify(value));
	}

	async function selectSheet(name: string) {
		// @ts-ignore
		const response = await Common.setReadColumns(name, fileWorkBook, headerRow);
		setImportWizardTable(response.rows);
		setImportWizardTableColumns(response.columns);
		setValidatedWizardTable(DeepCopy(response.rows));
	}

	function findSupplierId(supplierName: string) {
		//Check if its already a supplier code
		if (supplierName === undefined || supplierName === null || supplierName === "") return null;

		let supplierCodes: any = suppliers.table.find((item: any) => item.systemCode?.toLocaleLowerCase() === supplierName?.toLocaleLowerCase());
		if (supplierCodes) return supplierCodes.id;
		let supplier: any = suppliers.table.find((item: any) => item.name?.toLocaleLowerCase() === supplierName?.toLocaleLowerCase());
		if (supplier) return supplier.id;
		let supplierAltNames: any = supplierNameMappings.table.find((item: any) => item.alternateName?.toLocaleLowerCase() === supplierName?.toLocaleLowerCase());
		return supplierAltNames ? supplierAltNames.supplierId : null;
	}

	function Validate() {
		setValidating(true);
		let toProcessRows: any[] = DeepCopy(importWizardTable);

		// Batch the rows and create multiple web workers
		const BATCH_SIZE = 20;

		// let batchWorkers: Worker[] = [];
		// for (let i = 0; i < toProcessRows.length; i += BATCH_SIZE) {
		//     const batchedRows = toProcessRows.splice(i, BATCH_SIZE);
		//     const worker = new Worker(new URL('./validationWorker.ts', import.meta.url));
		//     worker.postMessage({ batchedRows: batchedRows, index: i, batchSize: BATCH_SIZE, validating: true });
		//     worker.onmessage = (event => {
		//         console.log("Message from worker: ",  event.data);
		//         worker.terminate();
		//     })

		//     batchWorkers.push(worker);
		// }

		// for (let i = 0; i < toProcessRows.length; i += BATCH_SIZE) {
		//     const batchedRows = toProcessRows.splice(i, BATCH_SIZE);
		//     // 1. Create new web worker
		//     // 2. Execute script
		//     // 3. Send batchedRows
		//     // 4. Wait for response.
		//     // 5. Promise.all??

		//     // const worker = new Worker('./public/validationWorker.ts');
		//     // worker.postMessage({ batchedRows: batchedRows, index: i, batchSize: BATCH_SIZE });
		//     // worker.onmessage = (event => {
		//     //     console.log("Message from worker: ",  event.data);
		//     //     worker.terminate();
		//     // })

		//     // batchWorkers.push(worker);
		// }

		// ! MOVE THIS CODE TO ANOTHER FILE TO USE WEB WORKER
		toProcessRows.forEach((row: any, i: number) => {
			if (closing.current) {
				return; //exit out, we're closing!
			}
			let valid: boolean = true;
			let errors: string[] = [];
			row.validationState = ValidationState.Validating;

			columnOptions.forEach((v, i) => {
				if (closing.current) {
					return; //exit out, we're closing!
				}

				row[v.key] = undefined; //Reset it before we set its value down below. Incase we changed our mind in the column selection
				let selected = columnValues[i];
				if (columnValuesRef.current[i] !== undefined) {
					if (typeof row[`col${selected}`] === "string") {
						row[`col${selected}`] = row[`col${selected}`].trim();
					}

					if (v.validation) {
						//@ts-ignore
						let val = v.validation(row[`col${parseInt(selected)}`], formatType);
						valid = valid && val.valid;
						if (!val.valid) errors.push(val.error);
						if (val.newValue) {
							row[`col${selected}`] = val.newValue;
							row[v.key] = val.newValue;
						} else {
							row[v.key] = row[`col${selected}`];
						}

						//Override the display, but in the background we'll display something else
						if (val.displayValue) {
							row[`col${selected}`] = val.displayValue;
						}
					} else {
						valid = valid && true;
						row[v.key] = row[`col${selected}`];
					}
				}
			});

			// Ensure that:
			// - If match by APN: Apn is supplied
			// - If match by PDE: Pde/Supplier are supplied
			// - If match by APN or PDE: Apn & Pde/Supplier are supplied
			let isValidForImportType: boolean = false;

			switch (importBy) {
				case ImportBy.APN:
					isValidForImportType = row.__apn;
					if (!isValidForImportType) errors.push("Apn missing");
					valid &&= isValidForImportType;
					break;
				case ImportBy.PDE:
					isValidForImportType = row.__pde && row.__supplier;
					if (!isValidForImportType) errors.push("Pde or supplier missing");
					valid &&= isValidForImportType;
					break;
				case ImportBy.ANP:
					isValidForImportType = (row.__pde && row.__supplier) || row.__apn;
					if (!isValidForImportType) errors.push("No apn or pde supplied");
					valid &&= isValidForImportType;
					break;
				default:
					break;
			}

			if (valid) {
				row.processed = ProcessingState.NotStarted;
				row.message = "";
				row.hasError = false;
				row.validationState = ValidationState.Valid;
			} else {
				row.processed = ProcessingState.Failed;
				row.message = errors.join(", ");
				row.hasError = true;
				row.validationState = ValidationState.Invalid;
			}
		});

		if (props.validateTable) {
			toProcessRows = props.validateTable(toProcessRows);
		}

		setValidatedWizardTable(toProcessRows);
		setReadyToValidate(false);
		setReadyToProcess(true);
		setValidating(false);
		setKeyword("");
	}

	const terminateWorkersRef = useRef<Function>();

	async function Process() {
		let toProcessRows: any[] = [...validatedWizardTable];
		setLoading(true);
		setIsProcessing(true);
		setValidatedWizardTable([]);
		setTotalStocksToImport(toProcessRows.length);

		const processWorkerData: ProcessWorkerRequestData = {
			data: toProcessRows,
			matchAgainst: matchAgainst,
			importBy: importBy ?? ImportBy.APN,
			apnColumn: apnColumn,
			pdeColumn: pdeColumn,
			supplierColumn: supplierColumn,
		};

		const { runWorkers, terminateWorkers } = runBatchWorkers<ProcessWorkerRequestData, any, ProcessWorkerResponseData, CommonColumnProperties[]>({
			workerData: processWorkerData,
			createWorker: () => new Worker(new URL("./processWorker.ts", import.meta.url), { type: "module" }),
			batchSize: 300,
			maxActiveWorkers: 15,
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				// Add rowIds to each entry so that when adding to the table we don't need to redraw everything
				const dataWithRowId =
					responseContext.response.data?.processedData.map((d, ix) => {
						return { ...d, rowId: responseContext.batchIndex + ix };
					}) ?? [];

				setValidatedWizardTable((prev) => {
					return prev.concat(dataWithRowId);
				});

				setFinalTable((prev) => {
					return prev.concat(dataWithRowId);
				});

				// Update # processed stocks
				setProcessedStocks((prev) => prev + dataWithRowId.length);

				// Update progress percentage
				setProgressPercentage((prev) => {
					return Math.min(100, prev + (1 / responseContext.numBatches) * 100);
				});

				return dataWithRowId;
			},
			onWorkersSuccess: async (workerResponses) => {
				const callbackData = workerResponses.flatMap((r) => (r.onWorkerResponseCallbackData ? r.onWorkerResponseCallbackData : []));
				props.postProcessStep && setFinalTable(props.postProcessStep(callbackData));
				setIsProcessing(false);
				setReadyToProcess(false);
				setLoading(false);
				setReadyToImport(true);
			},
			onWorkerError: (error, _) => {
				console.error("An error occurred in a processing web worker: ");
				console.error(error.message);
				props.handleClose();
				closing.current = true;
			},
		});

		terminateWorkersRef.current = terminateWorkers;

		await runWorkers;
	}

	useEffect(() => {
		// We're closing the import wizard
		if (closing.current) {
			// Terminate all workers, we no longer need to process things!
			terminateWorkersRef.current && terminateWorkersRef.current();
			setLoading(false);
			setIsProcessing(false);
			setReadyToProcess(false);
		}
	}, [closing.current]);

	const PreviewTopbarChild = () => {
		return (
			<>
				{fileWorkBook && (
					<Row className="d-flex justify-content-end mb-2 me-0">
						<Col md={4} className="m-0 ps-1 pe-0 pt-3">
							<Form.Label className="form-check-label d-block text-left toobar-select-label">Header</Form.Label>
							<Form.Select className="custom-select" onChange={(e) => setHeaderRow(parseInt(e.target.value))} value={headerRow} style={{ minHeight: "38px" }}>
								<option hidden value="">
									Select a sheet
								</option>
								<option value="0">No Header</option>
								{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n: number) => (
									<option value={n} key={n}>
										Row {n}
									</option>
								))}
							</Form.Select>
						</Col>
						<Col md={4} className="ps-1 pe-0 col-select pt-3">
							<Form.Label className="form-check-label d-block text-left toobar-select-label">Sheet</Form.Label>
							<Form.Select
								disabled={totalStocksToImport === 0 ? false : true}
								className="custom-select"
								onChange={(e) => setCurrentSheet(e.target.value)}
								value={currentSheet}
								style={{ minHeight: "38px" }}
							>
								<option value="">Select a sheet</option>
								{fileSheets.map((fileSheets: any, index: any) => {
									return (
										<option key={index} value={fileSheets}>
											{fileSheets}
										</option>
									);
								})}
							</Form.Select>
						</Col>
					</Row>
				)}
			</>
		);
	};

	const UploadFile = ({ next }: any) => {
		return (
			<div className="d-flex scrollable-container flex-column flex-grow" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<h5 className="header-title mb-3 mt-1">Upload File</h5>
				<FileUploader
					fileName={fileDetails.name}
					fileSize={fileDetails.size}
					onFileUpload={async (files: any) => {
						var response = await Common.HandleFile2(files);
						setFormatType(response.format);
						setFileSheets(response.sheets);
						setCurrentSheet(response.sheets[0]);
						setFileWorkbook(response.workbook);
						setFileLoaded(true);

						next();
						props.stepChange && props.stepChange(2);
						setStepNumber(2);
					}}
					fileDetails={async (files: any) => {
						let file = await files[0];
						setFileDetails({ name: file.path, size: file.formattedSize });
					}}
					onClose={Reset}
				/>
			</div>
		);
	};

	const SelectOptions = (next?: () => void, previous?: () => void, goToInitialStep?: () => void) => {
		const form: any = useRef(null);
		const [isValidated, setValidated] = useToggle();

		function handleFormSubmit() {
			handleSubmit(
				async (validData) => {
					if (props.additionalSettings) {
						props.onSettingsSubmit && props.onSettingsSubmit();
					}
					next && next();
					props.stepChange && props.stepChange(2);
					setStepNumber(2);
				},
				(invalidData) => {}
			)();
		}

		return (
			<Form onSubmit={handleFormSubmit} validated={isValidated} ref={form} className="d-flex flex-column flex-grow" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<h5 className="header-title mb-3 mt-1">Select Options</h5>

				<div className="mt-1 mb-0 shadow-none border card">
					<div className="p-2">
						<div className="align-items-center row">
							<div className="ps-0 ms-3 col">
								<a className="text-muted fw-bold" href="#">
									{fileDetails.name}
								</a>
								<p className="mb-0">
									<strong>{fileDetails.size}</strong>
								</p>
							</div>
							<div className="text-end col">
								<a
									className="btn btn-link btn-lg text-muted shadow-none"
									href="#"
									onClick={() => {
										goToInitialStep && goToInitialStep();
										Reset();
									}}
								>
									<i className="dripicons-cross"></i>
								</a>
							</div>
						</div>
					</div>
				</div>

				{matchAgainst !== "None" && (
					<>
						<Row className="mt-1 mb-2">
							{props.overrideImportBy === undefined && (
								<Col md={6} className="mt-2">
									<h5 className="header-title mb-3">Matching Criteria</h5>
									<Controller
										control={control}
										rules={{
											required: { value: true, message: "Please select a matching criteria" },
										}}
										render={({ field: { onChange, onBlur, value, ref }, fieldState, formState }) => {
											return (
												<ZSingleSelectDropdown
													options={Object.values(ImportBy)}
													placeholder="Select a matching criteria"
													value={value}
													validation={{
														invalid: errors.importBy != null,
														error: errors.importBy?.message,
													}}
													onBlur={onBlur}
													optionKey={(opt) => opt}
													disabled={() => false}
													display={(opt) => importByMap.get(ImportBy[opt as keyof typeof ImportBy]) ?? "Unknown"}
													onSelectionChange={(code) => {
														if (code) {
															setImportBy(ImportBy[code as keyof typeof ImportBy]);
															onChange(code);
														}
													}}
												/>
											);
										}}
										name={"importBy"}
									/>
								</Col>
							)}
							{/* <Col md={2} className="d-flex align-items-end mt-2" /> */}
							<Col md={6} className="mt-2">
								{/* <h5 className="header-title mb-3">Matching Against</h5>
                                <Form.Select
                                    disabled={matchAgainst !== "Pick"}
                                    value={matchAgainst}
                                    onChange={(e) => {
                                        setMatchAgainst(e.target.value as MatchAgainstOptions);
                                    }}
                                    style={{ minHeight: "38px" }}
                                >
                                    <option selected hidden value="Pick">Please Select</option>
                                    <option value="Products">System Stock</option>
                                    <option value="Stocks">Managed Stock</option>
                                    <option value="Both">System and Managed Stock</option>
                                </Form.Select> */}

								{props.additionalSettings && props.additionalSettings.length > 0 && (
									<>
										<h5 className="header-title mb-3">Additional Options</h5>
										{props.additionalSettings &&
											props.additionalSettings.map((option, index) => {
												return (
													<div key={option.key} className="pb-1">
														{option.element}
													</div>
												);
											})}
									</>
								)}
							</Col>
						</Row>
					</>
				)}

				<Row className="mt-auto">
					<Col xs={6}>
						<Button
							disabled={loading || !closeButton}
							variant="secondary"
							onClick={() => {
								goToInitialStep && goToInitialStep();
								Reset();
							}}
						>
							Reset
						</Button>
					</Col>
					<Col xs={6} className="d-flex" style={{ flex: 1 }}>
						<div style={{ display: "flex", flexDirection: "row", flex: 1, gap: "1rem" }}>
							<div style={{ flex: 1 }}></div>
							<Button variant="primary" className="flex-end" onClick={handleFormSubmit}>
								Next
							</Button>
						</div>
					</Col>
				</Row>
			</Form>
		);
	};

	const previewFile = (next?: () => void, previous?: () => void, goToInitialStep?: () => void) => {
		const requiredColumns = columnOptions
			.map((v, i) => {
				if (isColumnRequired(v)) {
					if (columnValues[i] === undefined) {
						return v.title;
					}
				}
			})
			.filter((v) => v !== undefined);

		return (
			<Form className="d-flex flex-column flex-grow">
				<h5 className="header-title mb-0">{readyToImport ? "Review/Import" : "Preview/Import Setup"}</h5>
				{/* <Table
                    isSortable={true}
                    pagination={false}
                    isSearchable={true}
                    disableExport={true}
                    columns={validationColumns}
                    topbarChild={<PreviewTopbarChild />}
                    loading={isProcessing}
                    data={validatedWizardTable}
                /> */}

				<Row className="d-flex align-items-end mb-1">
					<Col xs={12} md={6}>
						<Search
							mainSearchClass="mb-2"
							hideContains
							totalRows={validatedWizardTable.length}
							onSearch={(s) => {
								setKeyword(s.keyword ?? "");
							}}
						/>
					</Col>
					<Col xs={12} md={6}>
						<PreviewTopbarChild />
					</Col>
				</Row>
				<Row className="d-flex flex-grow flex-column" style={{ minHeight: "30vh" }}>
					<Col xs={12} className="d-flex flex-grow flex-column">
						<AgGrid
							ref={previewGridRef}
							rowHeight={AgGridRowHeight.Slim}
							rowData={validatedWizardTable.filter((x) => {
								return validationColumns.some((c) => {
									if (c.field && x[c.field] !== undefined && typeof x[c.field] === "string") {
										return (x[c.field] as string).containsKeyword(keyword);
									}
								});
							})}
							loading={isProcessing}
							pagination={"none"}
							columnDefs={validationColumns}
							handleSubmit={() => {}}
						/>
					</Col>
				</Row>
				<Row>
					<Col xs={12}>
						<ul className="list-inline wizard mb-0 mt-3">
							<li className="previous list-inline-item">
								<Button
									variant="secondary"
									disabled={loading}
									onClick={() => {
										goToInitialStep && goToInitialStep();
										Reset();
									}}
								>
									Reset
								</Button>
							</li>
							<li className="next list-inline-item float-end d-flex">
								{!readyToValidate && !readyToProcess && (
									<Alert variant="danger" className="me-2 mb-0 py-1 px-2 bg-transparent text-danger">
										<i className="mdi mdi-alert-circle-outline me-2"></i>
										Please select the required column(s) -
										{requiredColumns.map((v, i) => {
											return (
												<span key={i}>
													{` ${v}`}
													{requiredColumns.length > 1 ? requiredColumns.length - 1 !== i && "," : ""}
												</span>
											);
										})}
									</Alert>
								)}

								{!readyToProcess && (
									<>
										<Button
											variant="primary"
											className="flex-end"
											onClick={() => {
												previous && previous();
											}}
										>
											Prev
										</Button>

										<Button variant="info" style={{ alignItems: "center" }} className="btn ms-1 d-flex" disabled={validating || !readyToValidate} onClick={Validate}>
											{validating ? (
												<>
													<Spinner className="spinner-border-sm me-1" tag="span" color="white" />
												</>
											) : (
												<></>
											)}
											{validating ? <>Validating...</> : <>Validate</>}
										</Button>
									</>
								)}

								{readyToProcess && (
									<>
										{loading ? (
											<Button disabled variant="light" className="ms-auto">
												{processedStocks} of {totalStocksToImport}
											</Button>
										) : (
											<></>
										)}

										<Button
											variant="primary"
											className="flex-end me-1"
											onClick={() => {
												validateColumns();
												previous && previous();
											}}
										>
											Prev
										</Button>

										<Button
											variant="info"
											disabled={loading}
											onClick={() => {
												setStepNumber(3);
												props.stepChange && props.stepChange(3);
												next && next();
												Process();
											}}
										>
											{loading ? (
												<>
													<Spinner className="spinner-border-sm me-1" tag="span" color="white" />
												</>
											) : (
												<></>
											)}
											{loading ? <>Processing...</> : <>Process</>}
										</Button>
									</>
								)}
							</li>
						</ul>
					</Col>
				</Row>
			</Form>
		);
	};

	const ReviewPage = (next?: () => void, previous?: () => void, goToInitialStep?: () => void) => {
		const validLines = useMemo(() => finalTable.filter((i) => i.processed === ProcessingState.Done), [finalTable]);
		const invalidLines = useMemo(() => finalTable.filter((i) => !validLines.includes(i)), [finalTable, validLines]);

		useEffect(() => {
			const newReviewColumns: ZColDef[] = [
				...[
					props.matchedColumnOverride
						? props.matchedColumnOverride
						: {
								headerName: "Matched",
								field: "processed",
								pinned: true,
								cellRendererSelector: (params) => {
									if (params.value === ProcessingState.Processing) {
										return { component: "validCellRenderer", params: { loading: true } };
									} else {
										return {
											component: "validCellRenderer",
											params: { value: params.value === undefined ? params.value : params.value === ProcessingState.Done, validationError: params.data.message },
										};
									}
								},
								// Cell: (row: any) => <>
								//     {row.value === true && <i className="uil-times-circle"></i>}
								//     {row.value === false && <i className="uil-check"></i>}
								//     {row.value === null && <i className="uil-spinner"></i>}
								//     &nbsp;
								//     {row.row.original.message}
								// </>,
						  },
				],
				...importWizardTableColumns.map<ZColDef>((column: any, index: number) => {
					const newHeaderName = (fileWorkBook && currentSheet && importWizardTools.getFileWorkBookHerader(index, headerRow, fileWorkBook[currentSheet], fileWorkBookRange?.s?.c)) || "-";
					const columnHasMapping = columnValues.find((x) => x === index) != null;

					return {
						headerName: newHeaderName,
						sortable: true,
						hide: !columnHasMapping,
						field: column.accessor,
						minWidth: 100,
					};
				}),
			];

			invalidLinesRef.current?.updateColumnDefinitions(newReviewColumns);
			validLinesRef.current?.updateColumnDefinitions(newReviewColumns);
		}, [invalidLinesRef.current, validLinesRef.current, props.matchedColumnOverride]);

        const PreviewSummaryFooter = useMemo(() => {
			let addFilter = validLines.filter((x) => x.matchedAgainst === "None");
			let importFilter = validLines.filter((x) => x.matchedAgainst === "System");
			let updateFilter = validLines.filter((x) => x.matchedAgainst === "Managed");

            return (
                <div className="d-flex flex-col justify-content-between w-100">
					<ZTooltip text="Item(s) to be created">
						<span>
							<ZIcon iconClassName="conflict-alert-icon" icon={ZIconType.Add} variant="success" /> {addFilter.length}
						</span>
					</ZTooltip>
					<ZTooltip text="Item(s) to be imported">
						<span>
							<ZIcon iconClassName="conflict-alert-icon" icon={ZIconType.Upload} variant="success" /> {importFilter.length}
						</span>
					</ZTooltip>
					<ZTooltip text="Item(s) to be updated">
						<span>
							<ZIcon iconClassName="conflict-alert-icon" icon={ZIconType.Check} variant="success" /> {updateFilter.length}
						</span>
					</ZTooltip>
					<ZTooltip text="Item(s) with issues">
						<span>
							<ZIcon iconClassName="conflict-alert-icon" icon={ZIconType.Alert} variant="warning" /> {invalidLines.length}
						</span>
					</ZTooltip>
                </div>
			);
        }, [validLines, invalidLines]);

		return (
			<Form className="d-flex flex-column flex-grow">
				<div className="d-inline-flex align-items-center">
					<h5 className="header-title mb-3">Review/Import</h5>
				</div>
				<Row className="d-flex flex-grow flex-column" style={{ minHeight: "30vh" }}>
					<Col xs={12} className="d-flex flex-grow flex-column">
						<Tabs
							className="ag-grid-tabs-container"
							defaultActiveKey="valid-lines"
							id="uncontrolled-tab-example"
							onSelect={(e) => {
								switch (e) {
									case "valid-lines":
										setShowImportButton(true);
										break;
									case "invalid-lines":
									default:
										setShowImportButton(false);
										break;
								}
							}}
						>
							<Tab
								eventKey="valid-lines"
								onSelect={(e) => {
									setShowImportButton(true);
								}}
								title={`${validLines.length} Valid Lines`}
								className="ag-grid-tab"
							>
								<AgGrid
									ref={validLinesRef}
									rowHeight={AgGridRowHeight.Slim}
									rowData={validLines}
									loading={isProcessing}
									getRowId={(p) => p.data?.rowId ?? p.data?.id}
									pagination={"none"}
									columnDefs={reviewColumns}
								/>
							</Tab>
							<Tab
								eventKey="invalid-lines"
								onSelect={(e) => {
									setShowImportButton(false);
								}}
								title={`${invalidLines.length} Invalid Lines`}
								className="ag-grid-tab"
							>
								<AgGrid
									ref={invalidLinesRef}
									rowHeight={AgGridRowHeight.Slim}
									rowData={invalidLines}
									loading={isProcessing}
									getRowId={(p) => p.data?.rowId}
									pagination={"none"}
									columnDefs={reviewColumns}
								/>
							</Tab>
						</Tabs>
					</Col>
				</Row>
				<Row>
					<Col xs={12}>
						{progressPercentage > 0 && progressPercentage < 99 && (
							<ProgressBar
								animated
								striped
								label={`Processing... (${Math.ceil(Math.min(100, progressPercentage))}%)`}
								variant="info"
								now={Math.ceil(Math.min(100, progressPercentage))}
								className="my-1 progress-md"
							/>
						)}
						{props.importPercentage !== undefined && props.importPercentage > 0 && props.importPercentage < 99 && (
							<ProgressBar
								animated
								striped
								label={`${props.importLabel ? `${props.importLabel} (${Math.ceil(Math.min(100, props.importPercentage))}%)` : `${Math.ceil(Math.min(100, props.importPercentage))}%`}`}
								variant="info"
								now={Math.ceil(Math.min(100, props.importPercentage))}
								className="my-1 progress-md"
							/>
						)}
					</Col>
				</Row>
				{/** We add this extra summary component because we want to switch between 2 different layouts depending on screen size */}
				<Row className="mt-1 d-sm-none">
					<Col xs={12} className="d-flex align-items-center">
						{PreviewSummaryFooter}
					</Col>
				</Row>
				<Row className="mt-1">
					<Col xs={2}>
						<Button
							disabled={loading || !closeButton}
							variant="secondary"
							onClick={() => {
								goToInitialStep && goToInitialStep();
								Reset();
							}}
						>
							Reset
						</Button>
					</Col>
					<Col xs={10} className="d-flex justify-content-end gap-2 align-items-center">
						<span className="d-none d-sm-flex">{PreviewSummaryFooter}</span>
						{readyToImport ? (
							showImportButton ? (
								<Button
									onClick={async () => {
										setStepNumber(4);
										props.stepChange && props.stepChange(4);
										// setCloseButton(false);
										setLoading(true);
										let errorRows: any[] = [];
										if (props.import) {
											errorRows = props.import(validLines);
										} else if (props.importAsync) {
											errorRows = await props.importAsync(validLines);
										}
										if (errorRows.length > 0) {
											// We should compare validLines against the error rows and allow a retry of reimporting just those error rows
											setPostImportTable(errorRows);
											// setReimportableRows(validLines.filter(line => errorRows.map(er => er.id).includes(line.id)));
											next && next();
										} else {
											props.finish();
										}
										setLoading(false);
									}}
									variant="primary"
									disabled={loading || validLines.length === 0}
								>
									{loading && <Spinner className="spinner-border-sm me-1" tag="span" color="white" />}
									{loading ? <>Importing...</> : <>Import</>}
								</Button>
							) : (
								<DropdownButton variant="light" title="Export" className="btn-icon">
									<Dropdown.Item
										onClick={() => {
											invalidLinesRef.current?.exportToFile("csv", `invalid_lines_${FormatDateToFileName(new Date())}`, ["processed"]);
										}}
									>
										<i className="mdi mdi-file"></i> CSV File
									</Dropdown.Item>
									<Dropdown.Item
										onClick={() => {
											invalidLinesRef.current?.exportToFile("xlsx", `invalid_lines_${FormatDateToFileName(new Date())}`, ["processed"]);
										}}
									>
										<i className="mdi mdi-microsoft-excel"></i> Excel File
									</Dropdown.Item>
									<Dropdown.Item
										onClick={() => {
											invalidLinesRef.current?.exportToFile("pdf", `invalid_lines_${FormatDateToFileName(new Date())}`, ["processed"]);
										}}
									>
										<i className="mdi mdi-file-pdf-box"></i> PDF File
									</Dropdown.Item>
								</DropdownButton>
							)
						) : (
							<>
								{loading ? (
									<Button disabled variant="light">
										{processedStocks} of {totalStocksToImport}
									</Button>
								) : (
									<></>
								)}
								<Button disabled variant="info" className="ms-1">
									<Spinner className="spinner-border-sm me-1" tag="span" color="white" />
									Processing...
								</Button>
							</>
						)}
					</Col>
				</Row>
				{/* <ul className="list-inline wizard mb-0 mt-3">
                    <li className="previous list-inline-item">
                        <Button disabled={loading || !closeButton} variant="secondary" className="ms-1"
                            onClick={() => {
                                goToInitialStep && goToInitialStep();
                                Reset();
                            }}
                        >Reset</Button>
                    </li>
                    <li className="list-inline-item" style={{ width: "86%" }}>
                        {progressPercentage > 0 && progressPercentage < 99 &&
                            <ProgressBar animated striped label={`${progressPercentage}%`} variant="info" now={progressPercentage} className="mb-1 mt-1 ms-4 me-0 progress-sm" />
                        }
                        {props.importPercentage !== undefined && props.importPercentage > 0 &&
                            <ProgressBar animated striped label={`${props.importPercentage}%`} variant="info" now={props.importPercentage} className="mb-1 mt-1 ms-4 me-0  progress-sm" />
                        }
                    </li>
                    <li className="next list-inline-item float-end">
                        {readyToImport ?
                            <Button onClick={async () => {
                                setStepNumber(4);
                                setCloseButton(false);
                                setLoading(true);
                                let errorRows: any[] = [];
                                if (props.import) {
                                    errorRows = props.import(validLines);
                                } else if (props.importAsync) {
                                    errorRows = await props.importAsync(validLines);
                                }
                                if (errorRows.length > 0) {
                                    setPostImportTable(errorRows);
                                    next && next();
                                } else {
                                    props.finish();
                                }
                                setLoading(false);
                            }} variant="primary" disabled={loading}>
                                {loading && <Spinner className="spinner-border-sm me-1" tag="span" color="white" />}
                                {loading ? <>Importing...</> : <>Import</>}
                            </Button>
                            :
                            <Button disabled variant="info" className="ms-1">
                                <Spinner className="spinner-border-sm me-1" tag="span" color="white" />
                                Processing
                            </Button>
                        }
                    </li>
                </ul> */}
			</Form>
		);
	};

	const postImportPage = (next?: () => void, previous?: () => void) => {
		return (
			<Form className="d-flex flex-column flex-grow">
				<h5 className="header-title mb-1">Failed to import some stocks</h5>
				{/* <Table
                    isSortable={true}
                    isSearchable={true}
                    disableExport={true}
                    loading={isProcessing}
                    data={postImportTable}
                    columns={postImportColumns}
                /> */}
				<Row className="d-flex align-items-end">
					<Col xs={12} md={6}>
						<Search
							className="mb-2"
							hideContains
							totalRows={postImportTable.length}
							onSearch={(s) => {
								setKeyword(s.keyword ?? "");
							}}
						/>
					</Col>
					<Col xs={12} md={6}></Col>
				</Row>
				<AgGrid
					rowHeight={AgGridRowHeight.Slim}
					rowData={postImportTable.filter((x) => {
						return postImportColumns.some((c) => {
							if (c.field && x[c.field] !== undefined && typeof x[c.field] === "string") {
								return (x[c.field] as string).containsKeyword(keyword);
							}
						});
					})}
					loading={isProcessing}
					pagination={"none"}
					columnDefs={postImportColumns}
					handleSubmit={() => {}}
				/>
				<ul className="list-inline wizard mb-0 mt-3">
					<li className="previous list-inline-item"></li>
					<li className="next list-inline-item float-end">
						<Button variant="info" disabled={loading} onClick={props.finish}>
							{" "}
							Finish
						</Button>
					</li>
				</ul>
			</Form>
		);
	};

	return (
		<Modal
			centered
			show={props.display}
			onHide={() => {
				props.handleClose();
				closing.current = true;
			}}
			backdrop="static"
			dialogClassName="import-modal"
		>
			<Modal.Header closeButton={closeButton}>
				<Modal.Title>Import Wizard {props.title && `- ${props.title}`}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="d-flex flex-column pt-1" style={{ height: "80vh", overflowY: "auto", overflowX: "hidden" }}>
				<Wizard
					render={({ push }) => {
						const goToInitialStep = () => push("upload-file");
						return (
							<>
								<Row>
									<Col xs={12}>
										<ProgressStepBar now={stepNumber} steps={["Upload File", "Matching Criteria", "Preview/Import Setup", "Review/Import"]} />
									</Col>
								</Row>
								<Row className="d-flex flex-grow flex-column">
									<Col xs={12} className="d-flex flex-grow flex-column">
										<Steps>
											<Step id="upload-file" render={({ next }) => <UploadFile next={next} />} />
											<Step id="selectOptions" render={({ next, previous }) => SelectOptions(next, previous, goToInitialStep)} />
											<Step id="preview" render={({ next, previous }) => previewFile(next, previous, goToInitialStep)} />
											<Step id="review" render={({ next, previous }) => ReviewPage(next, previous, goToInitialStep)} />
											<Step id="postImport" render={({ next, previous }) => postImportPage(next, previous)} />
										</Steps>
									</Col>
								</Row>
							</>
						);
					}}
				/>
			</Modal.Body>
		</Modal>
	);
});
